<script setup>
import { documents } from '@/constants/documents';
import { navigation } from '@/constants/navigation';

const { scrollToHash } = useScrollTo();

const currentYear = computed(() => new Date().getFullYear());

function scrollToTop() {
  scrollToHash('#app');
}
</script>

<template>
  <footer class="layout-footer">
    <div class="layout-footer__container container">
      <div class="layout-footer__body">
        <div class="layout-footer__top">
          <div class="layout-footer__wrapper layout-footer__wrapper--title">
            <div class="layout-footer__title-part layout-footer__title-part--1">
              <span class="layout-footer__font layout-footer__font--title">
                Automate Your Travel Flow
              </span>
            </div>

            <div class="layout-footer__title-part layout-footer__title-part--2">
              <span class="layout-footer__font layout-footer__font--title">
                — with
              </span>

              <img
                src="/icons/centourio_logo-2.svg"
                alt="centour.io"
                class="layout-footer__icon layout-footer__icon--centourio"
              />
            </div>
          </div>

          <nav class="layout-footer__navigation">
            <CSpriteIcon
              v-for="item in 4"
              :key="item"
              class="layout-footer__icon layout-footer__icon--plus"
              :class="`$layout-footer__icon layout-footer__icon--plus--${item}`"
              name="plus"
            />

            <ul class="layout-footer__navigation-list">
              <li
                v-for="(navigationItem, index) in navigation"
                :key="index"
                class="layout-footer__navigation-item"
              >
                <NuxtLink
                  class="layout-footer__navigation-link"
                  :to="{ hash: `#${navigationItem.id}` }"
                >
                  <span
                    class="layout-footer__font layout-footer__font--navigation"
                  >
                    {{ navigationItem.label }}
                  </span>
                </NuxtLink>
              </li>
            </ul>
          </nav>
        </div>

        <div class="layout-footer__bottom">
          <div class="layout-footer__copyright">
            <span class="layout-footer__font layout-footer__font--copyright">
              © {{ currentYear }} Centour.io, All Rights Reserved
            </span>
          </div>

          <div class="layout-footer__wrapper layout-footer__wrapper--documents">
            <div class="layout-footer__documents">
              <ul class="layout-footer__documents-list">
                <li class="layout-footer__documents-item">
                  <a
                    class="layout-footer__document-link"
                    :href="documents.termsOfService.src"
                    target="_blank"
                  >
                    <span
                      class="layout-footer__font layout-footer__font--document"
                    >
                      {{ documents.termsOfService.label }}
                    </span>
                  </a>
                </li>

                <li class="layout-footer__documents-item">
                  <a
                    class="layout-footer__document-link"
                    :href="documents.privacyPolicy.src"
                    target="_blank"
                  >
                    <span
                      class="layout-footer__font layout-footer__font--document"
                    >
                      {{ documents.privacyPolicy.label }}
                    </span>
                  </a>
                </li>

                <li class="layout-footer__documents-item">
                  <a
                    :href="documents.cookiePolicy.src"
                    class="layout-footer__document-link"
                    target="_blank"
                  >
                    <span
                      class="layout-footer__font layout-footer__font--document"
                    >
                      {{ documents.cookiePolicy.label }}
                    </span>
                  </a>
                </li>
              </ul>
            </div>

            <UiButtonCircle
              class="layout-footer__button layout-footer__button--scroll-top"
              @click="scrollToTop"
            />
          </div>
        </div>

        <CPicture
          name="centurio_pin2"
          class="layout-footer__image layout-footer__image--pin"
        />
      </div>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.layout-footer {
  overflow: hidden;
  background: url('#{$images-path}/red_map_background.webp') center / cover
    no-repeat;

  @supports not (-webkit-touch-callout: none) {
    background-attachment: fixed;
  }

  &__font {
    display: block;

    &--title {
      font-size: em(28);
      font-weight: 800;
      line-height: 100%;

      @include media-breakpoint-down(md) {
        font-size: em(22);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(18);
      }
    }

    &--copyright {
      font-family: $font-family-second;
      font-size: em(14);
      font-weight: 500;
      line-height: 100%;

      @include media-breakpoint-down(md) {
        font-size: em(11);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(10);
      }
    }

    &--document {
      font-family: $font-family-second;
      font-size: em(14);
      font-weight: 500;
      line-height: 100%;

      @include media-breakpoint-down(md) {
        font-size: em(11);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(10);
      }
    }

    &--navigation {
      font-size: em(14);
      font-weight: 700;
      line-height: 100%;

      @include media-breakpoint-down(md) {
        font-size: em(11);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(9);
      }
    }
  }

  &__icon {
    pointer-events: none;

    &--centourio {
      width: em(200);
      height: em(45);
      margin-top: em(-3);
      margin-left: em(11);

      @include media-breakpoint-down(md) {
        width: em(164);
        height: em(38);
        margin-top: em(-5);
        margin-left: em(9);
      }

      @include media-breakpoint-down(sm) {
        width: em(130);
        height: em(30);
        margin-top: em(-3);
        margin-left: em(5);
      }
    }

    &--plus {
      position: absolute;
      width: em(40);
      height: em(40);
      opacity: 0.5;

      @include media-breakpoint-down(sm) {
        width: em(30);
        height: em(30);
      }

      &--1 {
        top: 0;
        left: 0;

        @include media-breakpoint-down(md) {
          top: em(23);
          left: em(-36);
        }

        @include media-breakpoint-down(sm) {
          top: em(21);
          left: em(-28);
        }
      }

      &--2 {
        top: 0;
        right: 0;

        @include media-breakpoint-down(md) {
          top: em(198);
          left: em(-36);
        }

        @include media-breakpoint-down(sm) {
          top: em(159);
          left: em(-28);
        }
      }

      &--3 {
        bottom: em(10);
        left: 0;

        @include media-breakpoint-down(md) {
          top: em(23);
          left: em(132);
        }

        @include media-breakpoint-down(sm) {
          top: em(21);
          left: em(107);
        }
      }

      &--4 {
        right: 0;
        bottom: em(10);

        @include media-breakpoint-down(md) {
          top: em(198);
          left: em(132);
        }

        @include media-breakpoint-down(sm) {
          top: em(159);
          left: em(107);
        }
      }
    }
  }

  &__container {
  }

  &__body {
    position: relative;
    padding-top: em(22);
    padding-bottom: em(18);

    @include media-breakpoint-down(sm) {
      padding-bottom: em(14);
    }
  }

  &__top {
    display: flex;
    align-items: center;
    height: em(255);
    padding-left: em(40);

    @include media-breakpoint-down(md) {
      flex-direction: column;
      align-items: flex-start;
      height: auto;
      padding: em(28) em(48) 0;
    }

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      align-items: flex-start;
      height: auto;
      padding: em(18) em(34) 0;
    }
  }

  &__wrapper {
    &--title {
      display: flex;
      flex-direction: column;
      width: em(480);

      @include media-breakpoint-down(md) {
        width: 100%;
      }
    }

    &--documents {
      display: flex;
      align-items: center;

      @include media-breakpoint-down(md) {
        margin-top: em(6);
      }
    }
  }

  &__title-part {
    &--1 {
      align-self: flex-start;
      margin-bottom: em(14);
      color: $color-white;

      @include media-breakpoint-down(md) {
        margin-bottom: em(12);
      }

      @include media-breakpoint-down(sm) {
        margin-bottom: em(10);
      }
    }

    &--2 {
      display: flex;
      align-self: flex-end;
      color: $color-white;
    }
  }

  &__navigation {
    position: relative;
    padding: em(40) em(40) em(50);
    margin-left: em(40);

    @include media-breakpoint-down(md) {
      padding: em(60) 0 0;
      margin-left: 0;
    }

    @include media-breakpoint-down(sm) {
      padding: em(48) 0 0;
    }
  }

  &__navigation-list {
  }

  &__navigation-item {
    width: em(190);

    &:not(:last-child) {
      margin-bottom: em(11);

      @include media-breakpoint-down(md) {
        margin-bottom: em(8);
      }

      @include media-breakpoint-down(sm) {
        margin-bottom: em(5);
      }
    }

    @include media-breakpoint-down(md) {
      width: em(152);
    }

    @include media-breakpoint-down(sm) {
      width: em(121);
    }
  }

  &__navigation-link {
    display: block;
    padding: em(6) em(20);
    color: $color-white;
    text-transform: uppercase;
    background-color: rgba($color-white, 0.1);
    backdrop-filter: blur(4px);
    border-radius: em(5);
    transition: background-color $time-fast ease-out;

    @include hover-active-focus {
      background-color: rgba($color-white, 0.2);
    }

    @include media-breakpoint-down(md) {
      padding: em(6) em(16);
    }

    @include media-breakpoint-down(sm) {
      padding: em(5) em(13);
    }
  }

  &__bottom {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: em(14) em(24) em(14) em(20);
    margin: em(28) em(-20) 0;
    background-color: rgba($color-white, 0.05);
    backdrop-filter: blur(5px);
    border-radius: em(16);

    @include media-breakpoint-down(md) {
      flex-direction: column;
      padding: em(11) em(16);
      margin: em(54) 0 0;
    }

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      padding: em(11) em(16);
      margin: em(42) 0 0;
    }
  }

  &__copyright {
    color: $text-dark-on-red;
    text-transform: uppercase;
  }

  &__documents {
    margin-right: em(20);

    @include media-breakpoint-down(md) {
      margin-right: em(24);
    }

    @include media-breakpoint-down(sm) {
      margin-right: 0;
    }
  }

  &__documents-list {
    display: flex;
  }

  &__documents-item {
    &:not(:last-child) {
      margin-right: em(20);

      @include media-breakpoint-down(md) {
        margin-right: em(16);
      }
    }
  }

  &__document-link {
    color: $text-dark-on-red;
    text-transform: uppercase;
    transition: ease-out color $time-fast;

    @include hover-active-focus {
      color: $color-white;
    }
  }

  &__button {
    &--scroll-top {
      @include media-breakpoint-down(sm) {
        position: absolute;
        top: em(8);
        right: em(3);
      }
    }
  }

  &__image {
    pointer-events: none;

    &--pin {
      position: absolute;
      bottom: em(-46);
      left: em(405);
      display: block;
      width: em(147);
      height: auto;

      @include media-breakpoint-down(md) {
        right: em(57);
        bottom: em(130);
        left: unset;
        width: em(117);
      }

      @include media-breakpoint-down(sm) {
        right: em(40);
        bottom: em(95);
        left: unset;
        width: em(94);
      }
    }
  }
}
</style>
